import React, { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { Modal } from 'antd';
import {
  arrowBackOutline,
  informationCircleOutline,
  documentTextOutline,
  shieldCheckmarkOutline,
  globeOutline,
  heartOutline,
  closeOutline,
  chevronForwardOutline,
} from 'ionicons/icons';
import './SettingsPage.css';

const TERMS_OF_USE = `TERMS OF USE

Last updated: March 2025

Welcome to SvgTrace! These Terms of Use ("Terms") govern your use of the SvgTrace application ("App") and services provided by SvgTrace ("we," "us," or "our").

BY USING THE APP, YOU AGREE TO THESE TERMS. IF YOU DO NOT AGREE, DO NOT USE THE APP.

1. ACCEPTANCE OF TERMS
By downloading, installing, or using SvgTrace, you agree to be bound by these Terms and our Privacy Policy. These Terms constitute a legally binding agreement between you and SvgTrace.

2. SUBSCRIPTION SERVICES
• Free access to basic conversion features
• Pro subscription for premium features (AI generation, retrace, modify, resize, bulk convert)
• Payment processed through Stripe (web) or App Store (iOS)
• Automatic renewal unless cancelled at least 24 hours before renewal
• Cancellation: Cancel anytime through your account settings or device subscription settings
• No refunds for partial subscription periods

3. ACCEPTABLE USE
You agree to use the App only for lawful purposes. You may not:
• Use the App for any illegal activity
• Violate any applicable laws or regulations
• Infringe on intellectual property rights
• Upload malicious content or viruses
• Attempt to reverse engineer the App
• Share your account credentials

4. CONTENT AND ACCURACY
• SVG conversion results are algorithmic and may not be pixel-perfect
• AI-generated content may vary in quality
• We are not responsible for output quality in all cases

5. INTELLECTUAL PROPERTY
• The App and all content are owned by SvgTrace
• You receive a limited license to use the App
• You retain ownership of your uploaded images and generated outputs

6. PRIVACY
Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information.

7. DISCLAIMERS
THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.

8. LIMITATION OF LIABILITY
TO THE MAXIMUM EXTENT PERMITTED BY LAW, SVGTRACE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE APP.

9. TERMINATION
We may suspend or terminate your access for violations of these Terms.

10. GOVERNING LAW
These Terms are governed by the laws of the State of Delaware, United States.

11. CONTACT US
For questions, contact us at: support@svgtrace.com

By using SvgTrace, you acknowledge that you have read, understood, and agree to these Terms of Use.`;

const AboutPage = ({ onBack }) => {
  const [termsOpen, setTermsOpen] = useState(false);

  const infoItems = [
    {
      icon: informationCircleOutline,
      iconBg: '#f1f5f9',
      iconColor: '#64748b',
      label: 'App Version',
      value: '1.0.0',
    },
    {
      icon: heartOutline,
      iconBg: '#fdf2f8',
      iconColor: '#ec4899',
      label: 'Built with',
      value: 'React + Capacitor',
    },
  ];

  const legalItems = [
    {
      icon: documentTextOutline,
      title: 'Terms of Use',
      action: () => setTermsOpen(true),
    },
    {
      icon: globeOutline,
      title: 'Visit our website',
      action: () => window.open('https://svgtrace.com', '_blank'),
    },
  ];

  return (
    <div className="settings-page">
      <div className="sp-header">
        <button className="sp-back-btn" onClick={onBack}>
          <IonIcon icon={arrowBackOutline} />
        </button>
        <h2 className="sp-header-title">About SvgTrace</h2>
      </div>

      <div className="sp-scroll">
        {/* App Branding */}
        <div className="sp-section" style={{ textAlign: 'center', padding: '28px 18px' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #f453c4 0%, #667eea 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px',
              fontSize: 30,
              color: '#fff',
              fontWeight: 800,
            }}
          >
            S
          </div>
          <h3
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: '#1e293b',
              margin: '0 0 4px',
            }}
          >
            SvgTrace
          </h3>
          <p style={{ fontSize: 14, color: '#94a3b8', margin: 0 }}>
            Image to SVG Converter & AI Design Tools
          </p>
        </div>

        {/* App Info */}
        <div className="sp-section">
          <p className="sp-section-title">App Information</p>
          {infoItems.map((item, i) => (
            <div key={i} className="sp-item sp-item--static">
              <div
                className="sp-item-icon"
                style={{ background: item.iconBg, color: item.iconColor }}
              >
                <IonIcon icon={item.icon} />
              </div>
              <div className="sp-item-body">
                <p className="sp-item-sub" style={{ margin: 0 }}>
                  {item.label}
                </p>
                <p className="sp-item-title">{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Legal */}
        <div className="sp-section">
          <p className="sp-section-title">Legal</p>
          {legalItems.map((item, i) => (
            <div key={i} className="sp-item" onClick={item.action}>
              <div
                className="sp-item-icon"
                style={{ background: '#f1f5f9', color: '#64748b' }}
              >
                <IonIcon icon={item.icon} />
              </div>
              <div className="sp-item-body">
                <p className="sp-item-title">{item.title}</p>
              </div>
              <IonIcon
                icon={chevronForwardOutline}
                className="sp-item-chevron"
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <p
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: '#cbd5e1',
            padding: '8px 0 24px',
            margin: 0,
          }}
        >
          &copy; {new Date().getFullYear()} SvgTrace. All rights reserved.
        </p>
      </div>

      {/* Terms Dialog */}
      <Modal
        open={termsOpen}
        onCancel={() => setTermsOpen(false)}
        footer={null}
        width={600}
        className="sp-dialog"
        centered
        destroyOnClose
      >
        <div className="sp-dialog-header">
          <div className="sp-dialog-header-left">
            <IonIcon icon={documentTextOutline} className="sp-dialog-header-icon" />
            <h3 className="sp-dialog-title">Terms of Use</h3>
          </div>
          <button className="sp-dialog-close" onClick={() => setTermsOpen(false)}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>
        <div className="sp-dialog-body">{TERMS_OF_USE}</div>
        <div className="sp-dialog-footer">
          <button className="sp-btn-primary" onClick={() => setTermsOpen(false)}>
            I Understand
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AboutPage;
